import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import * as api from './api';
import type { IdeaBuild } from './api';

// Shared Claude-build state (P5-11b): which repos have builds on (fetched once
// per page load), the latest build per idea for cards, and the badge they show.

let setupOnce: Promise<string[]> | null = null;

/** Repos with Claude builds on; [] when GitHub isn't set up, the plan isn't Pro, or none are on. */
export function enabledBuildRepos(): Promise<string[]> {
  setupOnce ??= api
    .getGithubStatus()
    .then((s) => (s.available && s.pro ? api.listBuildRepos().then((r) => r.repos.filter((x) => x.state === 'enabled').map((x) => x.repo)) : []))
    .catch(() => []);
  return setupOnce;
}

export function useEnabledBuildRepos(): string[] {
  const [repos, setRepos] = useState<string[]>([]);
  useEffect(() => {
    let live = true;
    enabledBuildRepos().then((r) => { if (live) setRepos(r); });
    return () => { live = false; };
  }, []);
  return repos;
}

const running = (b: IdeaBuild) => b.status !== 'completed';

/** Latest build per idea (running, or finished in the last day); polls every 30 s while one runs. */
export function useRecentBuilds(on: boolean): { builds: Map<number, IdeaBuild>; reload: () => void } {
  const [builds, setBuilds] = useState<Map<number, IdeaBuild>>(new Map());
  const reload = useCallback(() => {
    api.listRecentBuilds()
      .then((r) => setBuilds(new Map(r.builds.map((b) => [b.idea_id, b]))))
      .catch(() => { /* cards work without it */ });
  }, []);
  useEffect(() => { if (on) reload(); }, [on, reload]);
  const anyRunning = [...builds.values()].some(running);
  useEffect(() => {
    if (!on || !anyRunning) return;
    const t = setInterval(reload, 30_000);
    return () => clearInterval(t);
  }, [on, anyRunning, reload]);
  return { builds, reload };
}

export const BuildsContext = createContext<Map<number, IdeaBuild>>(new Map());
export const useIdeaBuild = (ideaId: number) => useContext(BuildsContext).get(ideaId);

export function buildLabel(b: IdeaBuild): string {
  if (b.status === 'requested' || b.status === 'queued') return 'Claude build queued';
  if (b.status === 'in_progress') return 'Claude is building…';
  if (b.conclusion === 'success') return b.pr_url ? 'Claude opened a PR' : 'Claude build finished';
  if (b.conclusion === 'cancelled') return 'Claude build cancelled';
  return 'Claude build failed';
}

/**
 * One line on a card or in the panel. `link` makes it open the PR, else the
 * run, else the issue; cards pass false (a card is already a button, and the
 * panel it opens has the links).
 */
export function BuildBadge({ build, link = true }: { build: IdeaBuild; link?: boolean }) {
  const href = build.pr_url ?? build.run_url ?? build.issue_url;
  const tone = running(build) ? 'build-badge-run' : build.conclusion === 'success' ? 'build-badge-ok' : 'build-badge-bad';
  const content = (
    <><span aria-hidden="true">{running(build) ? '◌' : build.conclusion === 'success' ? '✓' : '✗'}</span> {buildLabel(build)}</>
  );
  if (!link) return <span className={`build-badge ${tone}`}>{content}</span>;
  return (
    <a
      className={`build-badge ${tone}`}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {content}
    </a>
  );
}
